<template>
  <div>
    <!-- No data snackbar -->
    <v-snackbar v-model="noDataAlert" :timeout="3000" color="primary" location="top">
      <h3>No data available for this range</h3>
      <v-btn color="blue" variant="text" class="d-flex ml-auto" @click="noDataAlert = false"> Close </v-btn>
    </v-snackbar>
    <v-snackbar v-model="pdfTimeoutError" :timeout="3000" color="primary" location="top">
      <h3>Error downloading requested pdf. Please try again.</h3>
      <v-btn color="blue" variant="text" class="d-flex ml-auto" @click="pdfTimeoutError = false"> Close </v-btn>
    </v-snackbar>
    <!-- Chart -->
    <div class="sccWrapper">
      <div class="elevation-1 chartWrapper sccCard">
        <div class="chart-title text-center">Data Set segmentation</div>
        <base-chart :options="chartOptions" :scroll-options="chartSize" @click="(e) => chartClick(e)" class="mt-7" />
      </div>
    </div>
    <!-- Members Modal -->
    <v-dialog v-model="membersDialog" @click:outside="membersDialog = false" persistent max-width="80vw">
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div>
            <div>Member Analysis for the selected range:</div>
          </div>
          <div>
            <v-btn
              left
              color="primary"
              class="mr-4"
              :disabled="!provider"
              :loading="processingCSV"
              @click="exportMemberCSV()"
            >
              <v-icon small class="mr-3">fas fas fa-file-download</v-icon>Download
            </v-btn>

            <v-btn rounded small icon @click="membersDialog = false">
              <v-icon small>fa-times</v-icon>
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text>
          <AppTable :table-data="membersTableData" :disableSort="true" default-sort-column="dataId" />
        </v-card-text>
        <v-card-actions>
          <v-btn color="grey-darken-1" variant="text" @click="membersDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { Vue, Prop } from 'vue-property-decorator';
import { Options } from 'vue-class-component';
import BaseChart from '@/components/charts/BaseChart.vue';
import data from '@/packages/stubs/echarts/rangeArray';
import _ from 'lodash';
import Utils from '../../utils/Utils';
import * as AT from '@/modules/members/store/actionTypes';
import { GET_EXPORT_JOB_BY_ID } from '@/modules/reports/store/actionTypes';
import { GET_CSV_URL } from '@/modules/members/store/actionTypes';
import { Table, TableColumn, TableRow, TableColumnType } from '@/types';
import AppTable from '@/components/AppTable.vue';

@Options({
  components: {
    BaseChart,
    AppTable,
  },
})
export default class SchemeComparisonChart extends Vue {
  @Prop({
    default: () => ({}),
  })
  chartData!: any;

  @Prop({
    default: () => [],
  })
  provider?: number[];

  public chartSize: any = {
    chartWidth: 50,
    chartHeight: 30,
    unit: 'rem',
  };

  public membersDialog = false;

  public fetchingMembersAnalysis = false;

  public noDataAlert = false;

  public processingCSV = false;

  public selectedRange = [];

  public currentFilter: any = {};

  public pdfTimeoutError = false;

  public pagination = {
    page: 1,
    limit: 15,
  };

  private async fetchMembersAnalysis(data: any = {}) {
    this.fetchingMembersAnalysis = true;
    const analysis = await this.$store.dispatch(AT.FETCH_MEMBERS_ANALYSIS, data);
    this.fetchingMembersAnalysis = false;
    return analysis;
  }

  public get chartOptions() {
    return {
      // color: ['#1d8867', '#a78cc0'],
      color: [
        {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            {
              offset: 0,
              color: '#4ac29a', // color at 0% position
            },
            {
              offset: 1,
              color: '#92dac2', // color at 100% position
            },
          ],
        },
        {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            {
              offset: 0,
              color: '#d6c1e1', // color at 0% position
            },
            {
              offset: 1,
              color: '#ae83c4', // color at 100% position
            },
          ],
        },
      ],
      tooltip: {
        show: true,
        formatter: (params: any) =>
          `Savers: ${this.chartData[params.dataIndex].doc_count} <br> ${params.seriesName}: ${Utils.formatNumber(
            params.value * 100,
            '0.[00]'
          )}%`,
      },

      legend: {
        // data: ['Avg IRR', 'Avg Benchmark IRR'],
        textStyle: { fontSize: 10, fontWeight: 'bold', fontFamily: 'Open Sans' },
        padding: [0, 0, 0, 0],
        right: '10%',
      },

      xAxis: [
        {
          name: 'Data sets',
          nameLocation: 'middle',
          nameTextStyle: { color: '#6f6c76', padding: [14, 0, 0, 0], fontFamily: 'Open Sans', fontWeight: 'bolder' },
          type: 'category',
          axisTick: { show: false },
          axisLabel: {
            padding: [0, 0, 0, 0],
            fontWeight: '600',
            fontFamily: 'Open Sans',
            color: '#6f6c76',
            interval: false,
            formatter: (v: string) => v.toUpperCase(),
          },
          axisLine: { show: true, lineStyle: { color: '#ccc' } },
          splitLine: {
            show: false,
            lineStyle: {
              color: '#eee',
            },
          },
          data: this.schemeData.xlabels,
        },
      ],
      yAxis: {
        name: 'IRR',
        nameLocation: 'middle',
        nameRotate: 90,
        nameTextStyle: { color: '#6f6c76', padding: [0, 0, 30, 0], fontFamily: 'Open Sans', fontWeight: 'bolder' },
        type: 'value',
        axisLabel: {
          formatter: (v: any) => Utils.formatNumber(v * 100, '0.[00]') + '%',
          fontWeight: '600',
          fontFamily: 'Open Sans',
          color: '#6f6c76',
          interval: false,
        },
        axisLine: { show: true, lineStyle: { color: '#ccc' } },
        splitLine: {
          show: true,
          lineStyle: {
            color: '#eee',
          },
        },
      },
      series: [
        {
          name: 'Avg IRR',
          type: 'bar',
          barGap: 0,
          data: this.schemeData.avg_airr,
          label: {
            show: true,
            rotate: 90,
            position: 'left',
            align: 'center',
            color: '#ae83c4',
            distance: 5,
            verticalAlign: 'bottom',
            rich: { name: { fontFamily: 'Open Sans', color: '#6f6c76' } },
            textBorderColor: '#fff',
            formatter: (params: any) =>
              `Score: ${
                this.chartData[params.dataIndex].avg_score || this.chartData[params.dataIndex].avg_score === 0
                  ? this.chartData[params.dataIndex].avg_score.toFixed(0)
                  : 'NA'
              }`,
          },
        },
        {
          name: 'Avg Benchmark IRR',
          type: 'bar',
          barGap: 0,
          data: this.schemeData.avg_birr,
          label: {
            show: true,
            position: 'top',
            color: '#ae83c4',
            rich: { name: { fontFamily: 'Open Sans', color: '#6f6c76' } },
            textBorderColor: '#fff',
            formatter: (params: any) => `${this.chartData[params.dataIndex].doc_count}`,
          },
        },
      ],
      barWidth: 30,
      barGap: '20%',
      barCategoryGap: '10%',
    };
  }

  public async exportMemberCSV() {
    //PDF Export Polling with 2sec interval and 16sec Timout
    let job = { id: -1 };

    this.processingCSV = true;
    job = await this.$store.dispatch(AT.EXPORT_MEMBERS_ANALYSIS, {
      page: 1,
      limit: -1,
      providers: this.currentFilter.providers,
      schemes: this.currentFilter.schemes,
    });

    let pdfFetchInterval = setInterval(async () => {
      let response = await this.$store.dispatch(GET_EXPORT_JOB_BY_ID, job.id);
      if (response[0].status === 'completed') {
        const url = await this.$store.dispatch(GET_CSV_URL, job.id);
        await window.open(url);
        this.processingCSV = false;
        clearInterval(pdfFetchInterval);
      }
      return response;
    }, Utils.pdfInterval);
    setTimeout(() => {
      clearInterval(pdfFetchInterval);
      if (this.processingCSV) {
        this.pdfTimeoutError = true;
      }
      this.processingCSV = false;
    }, Utils.pdfTimeout);
  }

  public async chartClick(e: any) {
    this.noDataAlert = false;
    const index = await e.dataIndex;
    const scheme = this.chartData[index].id;
    const appliedFilters = {
      providers: [this.provider],
      schemes: [scheme],
    };
    this.currentFilter = {
      ...this.pagination,
      ...appliedFilters,
    };
    const analysis = await this.fetchMembersAnalysis(this.currentFilter);
    analysis.length ? (this.membersDialog = true) : (this.noDataAlert = true);
  }

  public get schemeData() {
    var xlabels = this.chartData.map((item: any) => item.name);
    var avg_airr = this.chartData.map((item: any) => item.avg_airr);
    var avg_birr = this.chartData.map((item: any) => item.avg_birr);

    const scheme = {
      xlabels,
      avg_airr,
      avg_birr,
    };
    return scheme;
  }

  public get memberAnalysis(): any[] | null {
    return this.$store.getters.memberAnalysis && this.$store.getters.memberAnalysis.data;
  }

  public get membersTableData(): Table {
    const data = this.memberAnalysis ? this.memberAnalysis : [];
    const rows = data.map((it) => {
      return {
        dataId: it.dataId,
        // fundName: it.fund.name,
        firstContributionDate: it.firstContributionDate,
        score: it.scores.score || it.scores.score === 0 ? parseInt(it.scores.score) : '-',
        totalContributionAmount: it.totalContributionAmount,
        navAmount: it.analysis.navAmount,
        navDate: it.analysis.navDate,
        irr: it.analysis.irr,
        benchmarkIrr: it.analysis.benchmarkIrr,
        benchmarkNavAmount: it.analysis.benchmarkNavAmount,
      };
    });
    return {
      columns: [
        {
          value: 'dataId',
          title: 'Saver ID',
          type: TableColumnType.NUMBER,
        },
        // {
        //   value: 'fundName',
        //   text: 'Fund Name',
        //   type: TableColumnType.STRING
        // },
        {
          value: 'score',
          title: 'AW Score',
          type: TableColumnType.NUMBER,
        },
        {
          value: 'firstContributionDate',
          title: 'First Contribution Date',
          type: TableColumnType.DATE,
          formatter: (v: any) => Utils.formatDate(new Date(v), 'MMM dd, yyyy'),
        },
        {
          value: 'navDate',
          title: 'NAV Date',
          type: TableColumnType.DATE,
          formatter: (v: any) => Utils.formatDate(new Date(v), 'MMM dd, yyyy'),
        },
        {
          value: 'totalContributionAmount',
          title: 'Total Contribution Amount',
          type: TableColumnType.NUMBER,
          formatter: (n) => Utils.formatNumber(n, '$0.00a'),
        },
        {
          value: 'navAmount',
          title: 'NAV Amount',
          type: TableColumnType.NUMBER,
          formatter: (n) => Utils.formatNumber(n, '$0.00a'),
        },
        {
          value: 'benchmarkNavAmount',
          title: 'Benchmark Nav Amount',
          type: TableColumnType.NUMBER,
          formatter: (n) => Utils.formatNumber(n, '$0.00a'),
        },
        {
          value: 'irr',
          title: 'IRR (%)',
          type: TableColumnType.NUMBER,
          formatter: (v: any) => (v * 100).toFixed(2),
        },
        {
          value: 'benchmarkIrr',
          title: 'Benchmark IRR (%)',
          type: TableColumnType.NUMBER,
          formatter: (v: any) => (v * 100).toFixed(2),
        },
      ],
      rows,
    };
  }
}
</script>

<style lang="scss">
.sccWrapper {
  padding: 1rem 0rem !important;
}
</style>
